import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  MapPin,
  Calendar,
  Building2,
  Radio,
  Wifi,
  PhoneCall,
  ShieldCheck,
  RotateCw,
  Printer,
  Sparkles,
  Info,
} from 'lucide-react';
import { Badge4x6Data } from '../lib/api';

export interface BadgeCard4x6Props {
  badge: Partial<Badge4x6Data> & {
    badgeCode: string;
    primaryRole?: string;
    role?: string;
    fullName?: string;
    name?: string;
    companyName?: string | null;
    company?: string | null;
    jobTitle?: string | null;
    avatarUrl?: string | null;
    photoUrl?: string | null;
    roles?: string[];
    secondaryRoles?: string[];
    stallNumber?: string | null;
    nfcTagId?: string | null;
    ticketType?: string | null;
    tier?: string;
  };
  showBackDefault?: boolean;
  allowFlip?: boolean;
  scale?: number;
}

export const BadgeCard4x6: React.FC<BadgeCard4x6Props> = ({
  badge,
  showBackDefault = false,
  allowFlip = true,
  scale = 1,
}) => {
  const [isFlipped, setIsFlipped] = useState(showBackDefault);

  const primaryRole = badge.primaryRole || badge.role || 'trade_visitor';
  const fullName = badge.fullName || badge.name || 'Attendee';
  const companyName = badge.companyName ?? badge.company ?? '';
  const avatarUrl = badge.avatarUrl ?? badge.photoUrl ?? null;
  const roles = badge.roles || badge.secondaryRoles || [];

  const getRoleBandStyles = (role?: string) => {
    switch ((role || '').toLowerCase()) {
      case 'vip':
        return {
          bandBg: 'bg-[#C5A059]',
          bandText: 'text-white',
          borderCol: 'border-[#C5A059]',
          badgeTag: 'bg-[#FAF5EA] text-[#785613] border-[#E6C687]',
          label: badge.theme?.label || 'VIP',
        };
      case 'speaker':
        return {
          bandBg: 'bg-rose-700',
          bandText: 'text-white',
          borderCol: 'border-rose-700',
          badgeTag: 'bg-rose-50 text-rose-800 border-rose-200',
          label: badge.theme?.label || 'SPEAKER',
        };
      case 'organiser':
      case 'organizer':
        return {
          bandBg: 'bg-slate-900',
          bandText: 'text-white',
          borderCol: 'border-slate-900',
          badgeTag: 'bg-slate-100 text-slate-900 border-slate-300',
          label: badge.theme?.label || 'ORGANISER',
        };
      case 'media':
        return {
          bandBg: 'bg-orange-600',
          bandText: 'text-white',
          borderCol: 'border-orange-600',
          badgeTag: 'bg-orange-50 text-orange-800 border-orange-200',
          label: badge.theme?.label || 'MEDIA',
        };
      case 'sponsor':
        return {
          bandBg: 'bg-indigo-700',
          bandText: 'text-white',
          borderCol: 'border-indigo-700',
          badgeTag: 'bg-indigo-50 text-indigo-800 border-indigo-200',
          label: badge.theme?.label || 'SPONSOR',
        };
      case 'exhibitor':
        return {
          bandBg: 'bg-sky-600',
          bandText: 'text-white',
          borderCol: 'border-sky-600',
          badgeTag: 'bg-sky-50 text-sky-800 border-sky-200',
          label: badge.theme?.label || 'EXHIBITOR',
        };
      case 'delegate':
      case 'conference_delegate':
        return {
          bandBg: 'bg-purple-700',
          bandText: 'text-white',
          borderCol: 'border-purple-700',
          badgeTag: 'bg-purple-50 text-purple-800 border-purple-200',
          label: badge.theme?.label || 'DELEGATE',
        };
      case 'student_innovator':
        return {
          bandBg: 'bg-lime-600',
          bandText: 'text-white',
          borderCol: 'border-lime-600',
          badgeTag: 'bg-lime-50 text-lime-800 border-lime-200',
          label: badge.theme?.label || 'STUDENT INNOVATOR',
        };
      case 'contractor':
        return {
          bandBg: 'bg-yellow-500',
          bandText: 'text-slate-950',
          borderCol: 'border-yellow-500',
          badgeTag: 'bg-yellow-50 text-yellow-800 border-yellow-300',
          label: badge.theme?.label || 'CONTRACTOR',
        };
      case 'visitor':
      default:
        return {
          bandBg: 'bg-[#0A3D27]',
          bandText: 'text-white',
          borderCol: 'border-[#0A3D27]',
          badgeTag: 'bg-emerald-50 text-[#0A3D27] border-emerald-200',
          label: badge.theme?.label || (badge.ticketType === 'executive' ? 'EXECUTIVE' : 'TRADE VISITOR'),
        };
    }
  };

  const style = getRoleBandStyles(primaryRole);
  const qrLink = `https://iiab-platform-web-dev.pages.dev/b/${badge.badgeCode}`;

  return (
    <div className="flex flex-col items-center">
      {/* Flip Control on screen */}
      {allowFlip && (
        <div className="mb-2 flex items-center gap-2 print:hidden text-xs">
          <button
            type="button"
            onClick={() => setIsFlipped(!isFlipped)}
            className="flex items-center gap-1.5 px-3 py-1 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-lg font-medium shadow-2xs cursor-pointer transition-colors"
          >
            <RotateCw className="w-3.5 h-3.5 text-[#0A3D27]" />
            <span>Show {isFlipped ? 'Front Design' : 'Back Design (Schedule & Emergency)'}</span>
          </button>
        </div>
      )}

      {/* 4x6 Physical Badge Container */}
      <div
        style={{
          width: '4in',
          height: '6in',
          transform: scale !== 1 ? `scale(${scale})` : undefined,
          transformOrigin: 'top center',
        }}
        className={`relative bg-white text-slate-900 border-2 ${style.borderCol} rounded-2xl shadow-xl overflow-hidden flex flex-col justify-between print:shadow-none print:m-0 print:border-black print:rounded-none`}
      >
        {!isFlipped ? (
          /* ================= FRONT SIDE (§6.1) ================= */
          <div className="h-full flex flex-col justify-between">
            {/* Lanyard Slot Guide Mark */}
            <div className="w-14 h-2.5 bg-slate-100 border border-slate-300 rounded-full mx-auto mt-2 print:border-black" />

            {/* Top Event Header */}
            <div className="px-6 pt-3 pb-2 text-center border-b border-slate-100 print:border-slate-300">
              <div className="flex items-center justify-center gap-2 mb-1">
                <img
                  src="/logo/iiab_logo.png"
                  alt="IIAB Expo Logo"
                  className="h-9 w-auto object-contain print:h-8"
                />
                <div className="text-left">
                  <h1 className="text-xs font-black tracking-wider text-slate-900 uppercase leading-none">
                    IIAB EXPO 2027
                  </h1>
                  <span className="text-[9px] font-bold text-slate-500 uppercase">
                    5th Edition • BKC, Mumbai
                  </span>
                </div>
              </div>
              <p className="text-[9.5px] font-medium text-slate-600 flex items-center justify-center gap-2">
                <span className="flex items-center gap-0.5">
                  <Calendar className="w-2.5 h-2.5 text-[#0A3D27]" />
                  27–28 July 2027
                </span>
                <span>•</span>
                <span className="flex items-center gap-0.5">
                  <MapPin className="w-2.5 h-2.5 text-[#0A3D27]" />
                  JWCC Pavilion 3
                </span>
              </p>
            </div>

            {/* Middle Profile & Details Box */}
            <div className="px-6 py-2 flex flex-col items-center flex-1 justify-center space-y-2.5">
              {/* Photo Frame or Monogram */}
              <div className="w-20 h-24 rounded-xl border-2 border-slate-200 overflow-hidden bg-slate-100 flex items-center justify-center shadow-xs print:border-slate-400">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={fullName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#0A3D27]/10 to-[#FAF5EA] flex flex-col items-center justify-center text-center p-1">
                    <span className="text-2xl font-black text-[#0A3D27]">
                      {fullName
                        .split(' ')
                        .map((n) => n[0])
                        .slice(0, 2)
                        .join('')}
                    </span>
                    <span className="text-[8px] font-mono text-slate-400">IIAB PASS</span>
                  </div>
                )}
              </div>

              {/* Attendee Name (Legible from 2 meters) */}
              <div className="text-center w-full px-1">
                <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight leading-tight line-clamp-2">
                  {fullName}
                </h2>
                {badge.jobTitle && (
                  <p className="text-xs font-semibold text-slate-600 mt-0.5 line-clamp-1">
                    {badge.jobTitle}
                  </p>
                )}
                <p className="text-xs font-black text-[#0A3D27] mt-0.5 line-clamp-1">
                  {companyName || 'Independent Participant'}
                </p>
              </div>

              {/* QR Code & NFC Target Area */}
              <div className="w-full grid grid-cols-2 gap-3 items-center pt-1 border-t border-slate-100">
                {/* QR Code */}
                <div className="flex flex-col items-center">
                  <div className="p-1.5 bg-white border border-slate-300 rounded-xl shadow-2xs">
                    <QRCodeSVG
                      value={qrLink}
                      size={84}
                      level="H"
                      marginSize={0}
                      bgColor="#ffffff"
                      fgColor="#000000"
                    />
                  </div>
                  <span className="text-[8px] font-mono font-bold text-slate-600 mt-1 uppercase tracking-wider">
                    {badge.badgeCode}
                  </span>
                </div>

                {/* NFC Indicator Target */}
                <div className="flex flex-col items-center text-center justify-center p-2 rounded-xl bg-slate-50 border border-dashed border-slate-300 print:bg-white">
                  <div className="w-10 h-10 rounded-full border-2 border-[#0A3D27] flex items-center justify-center text-[#0A3D27] mb-1">
                    <Radio className="w-5 h-5 animate-pulse" />
                  </div>
                  <span className="text-[9px] font-black uppercase text-slate-800 tracking-wider">
                    ◉ NFC TAP
                  </span>
                  <span className="text-[7.5px] text-slate-500 font-medium leading-tight">
                    {badge.nfcTagId ? 'Tag Linked & Active' : 'Sticker sits behind mark'}
                  </span>
                </div>
              </div>

              {/* Secondary Roles / Stall Identifier */}
              <div className="w-full flex flex-wrap items-center justify-center gap-1.5 pt-0.5">
                {badge.stallNumber && (
                  <span className="px-2 py-0.5 rounded-md bg-sky-50 border border-sky-300 text-sky-800 text-[9px] font-black uppercase">
                    {`Stall ${badge.stallNumber}`}
                  </span>
                )}
                {roles &&
                  roles
                    .filter((r) => r.toLowerCase() !== primaryRole.toLowerCase())
                    .map((role) => (
                      <span
                        key={role}
                        className="px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-slate-700 text-[8.5px] font-bold uppercase"
                      >
                        {`+${role.replace('_', ' ')}`}
                      </span>
                    ))}
              </div>
            </div>

            {/* Bottom Primary Role Color Band (§6.1 & §6.3) */}
            <div className={`w-full py-3 text-center ${style.bandBg} ${style.bandText} tracking-widest font-black text-sm uppercase shadow-inner print:text-black`}>
              <span>{style.label}</span>
            </div>
          </div>
        ) : (
          /* ================= BACK SIDE (§6.2) ================= */
          <div className="h-full flex flex-col justify-between p-6 text-xs text-slate-700 bg-slate-50/50">
            {/* Top Venue & Helpdesk Info */}
            <div className="border-b border-slate-200 pb-3 space-y-1">
              <h3 className="font-black text-slate-900 text-sm uppercase tracking-wide">
                Exhibition & Conference Guide
              </h3>
              <p className="text-[10px] text-slate-500">
                Jio World Convention Centre, Pavilion 3, BKC, Mumbai
              </p>
            </div>

            {/* Daily Hours & Key Venues */}
            <div className="space-y-2 text-[10px]">
              <div className="bg-white p-2.5 rounded-xl border border-slate-200 space-y-1">
                <span className="font-bold text-slate-900 block">Operating Timings</span>
                <p>Day 1: Tuesday, 27 July 2027 • 10:00 AM – 06:00 PM</p>
                <p>Day 2: Wednesday, 28 July 2027 • 10:00 AM – 06:00 PM</p>
                <p className="text-[9px] text-[#0A3D27] font-semibold">
                  B2B Networking Slots: 10:00 AM – 05:30 PM (30-min intervals)
                </p>
              </div>

              {/* Wi-Fi Details */}
              <div className="bg-white p-2.5 rounded-xl border border-slate-200 flex items-center justify-between">
                <div>
                  <span className="font-bold text-slate-900 flex items-center gap-1">
                    <Wifi className="w-3 h-3 text-[#0A3D27]" />
                    <span>Venue Complimentary Wi-Fi</span>
                  </span>
                  <p className="font-mono text-slate-600">SSID: <span className="font-bold">JWCC_IIAB2027</span></p>
                </div>
                <span className="text-[9px] font-mono bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                  Open Ingress
                </span>
              </div>

              {/* Emergency & Support Contacts */}
              <div className="bg-white p-2.5 rounded-xl border border-slate-200 space-y-1">
                <span className="font-bold text-slate-900 flex items-center gap-1">
                  <PhoneCall className="w-3 h-3 text-rose-600" />
                  <span>On-Site Emergency & Duty Officer</span>
                </span>
                <p>Helpdesk Counter: <span className="font-semibold text-slate-900">Pavilion 3 Lobby</span></p>
                <p>Duty Helpline: <span className="font-mono font-bold text-slate-900">+91 22 6987 5000</span></p>
              </div>
            </div>

            {/* Bottom Footer & Sustainability */}
            <div className="border-t border-slate-200 pt-3 text-[9px] text-slate-500 space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-700">Official Pass SAC: 998596</span>
                <span className="font-mono font-bold">18% GST Applicable</span>
              </div>
              <p className="text-[8px] text-center text-slate-400">
                Please return badge ribbon at the exit turnstile recycling station upon event conclusion.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BadgeCard4x6;
